import React from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import styles from './PageCardGrid.module.css';

const grid = cva(styles.base);

export interface PageCardGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Minimum card width — number is treated as px, string is used verbatim */
  minWidth?: number | string;
  /** Gap between cards (CSS length or number of pixels). Defaults to var(--space-4). */
  gap?: number | string;
  children: React.ReactNode;
}

function toLength(v: number | string): string {
  return typeof v === 'number' ? `${v}px` : v;
}

/**
 * PageCardGrid — responsive CSS-grid wrapper for PageCard items.
 *
 * Sets `--page-card-min-width` and uses `repeat(auto-fill, minmax(...))`.
 * Built-in responsive overrides: 200px min at ≤1024px, 160px min at ≤768px.
 */
export function PageCardGrid({
  minWidth = 180,
  gap,
  className,
  style,
  children,
  ...props
}: PageCardGridProps) {
  const cssVars: React.CSSProperties = {
    '--page-card-min-width': toLength(minWidth),
    ...(gap !== undefined ? { '--page-card-grid-gap': toLength(gap) } : {}),
  } as React.CSSProperties;

  return (
    <div
      className={clsx(grid(), className)}
      style={{ ...cssVars, ...style } as React.CSSProperties}
      {...props}
    >
      {children}
    </div>
  );
}
